/**
 * Ollama Service
 * Handles communication with Ollama API for AI-powered features
 *
 * Setup:
 * 1. For local development: Run Ollama locally (http://localhost:11434)
 * 2. For production: Set REACT_APP_OLLAMA_API_URL in .env
 *
 * Example .env:
 * REACT_APP_OLLAMA_API_URL=https://your-ionos-vps.com/api/ollama
 */

const OLLAMA_API_URL = process.env.REACT_APP_OLLAMA_API_URL || 'http://localhost:11434';
const DEFAULT_MODEL = process.env.REACT_APP_OLLAMA_MODEL || 'llama3.1';

/**
 * Check if Ollama is available
 */
export const checkOllamaStatus = async () => {
  try {
    const response = await fetch(`${OLLAMA_API_URL}/api/tags`);
    if (!response.ok) return { available: false, models: [] };

    const data = await response.json();
    return {
      available: true,
      models: data.models || []
    };
  } catch (error) {
    console.warn('Ollama not available:', error.message);
    return { available: false, models: [], error: error.message };
  }
};

/**
 * Generate text with Ollama
 * @param {string} prompt - The prompt for the AI
 * @param {string} model - Model to use (default: llama3.2)
 * @param {object} options - Additional options
 */
export const generateText = async (prompt, model = DEFAULT_MODEL, options = {}) => {
  try {
    const response = await fetch(`${OLLAMA_API_URL}/api/generate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        model,
        prompt,
        stream: false,
        options: {
          temperature: options.temperature || 0.7,
          top_p: options.top_p || 0.9,
          ...options
        }
      })
    });

    if (!response.ok) {
      throw new Error(`Ollama API error: ${response.status}`);
    }

    const data = await response.json();
    return {
      success: true,
      text: data.response,
      model: data.model
    };
  } catch (error) {
    console.error('Ollama generation error:', error);
    return {
      success: false,
      error: error.message,
      text: ''
    };
  }
};

/**
 * AI Features for ZenEditor
 */

/**
 * Improve text (grammar, style, clarity)
 */
export const improveText = async (text, model = DEFAULT_MODEL) => {
  const prompt = `Verbessere den folgenden Text. Korrigiere Grammatik, Stil und Klarheit. Behalte die ursprüngliche Bedeutung bei. Gib nur den verbesserten Text zurück, ohne Erklärungen.

Text:
${text}

Verbesserter Text:`;

  return generateText(prompt, model, { temperature: 0.5 });
};

/**
 * Continue/generate text based on context
 */
export const continueText = async (context, model = DEFAULT_MODEL) => {
  const prompt = `Setze den folgenden Text fort. Schreibe im gleichen Stil und Ton weiter. Gib nur die Fortsetzung zurück, ohne den ursprünglichen Text zu wiederholen.

Text:
${context}

Fortsetzung:`;

  return generateText(prompt, model, { temperature: 0.8 });
};

/**
 * Create summary of text
 */
export const summarizeText = async (text, model = DEFAULT_MODEL) => {
  const prompt = `Fasse den folgenden Text prägnant zusammen. Behalte die wichtigsten Punkte bei. Gib nur die Zusammenfassung zurück.

Text:
${text}

Zusammenfassung:`;

  return generateText(prompt, model, { temperature: 0.5 });
};

/**
 * Suggest Markdown formatting for text
 */
export const suggestMarkdownFormatting = async (text, model = DEFAULT_MODEL) => {
  const prompt = `Analysiere den folgenden Text und schlage passende Markdown-Formatierungen vor. Strukturiere den Text mit Überschriften, Listen, Zitaten, etc. Gib nur den formatierten Markdown-Text zurück.

Text:
${text}

Markdown-formatierter Text:`;

  return generateText(prompt, model, { temperature: 0.6 });
};

/**
 * CODE-SPECIFIC AI FEATURES
 * Best used with CodeLlama model
 */

/**
 * Explain code - What does this code do?
 */
export const explainCode = async (code, model = 'codellama') => {
  const prompt = `Erkläre den folgenden Code auf Deutsch. Beschreibe was er macht, wie er funktioniert und wichtige Details. Sei präzise aber verständlich.

Code:
\`\`\`
${code}
\`\`\`

Erklärung:`;

  return generateText(prompt, model, { temperature: 0.3 });
};

/**
 * Complete code - Auto-complete based on context
 */
export const completeCode = async (context, model = 'codellama') => {
  const prompt = `Vervollständige den folgenden Code. Gib nur den fehlenden Code zurück, ohne Erklärungen.

Code:
\`\`\`
${context}
\`\`\`

Vervollständigung:`;

  return generateText(prompt, model, { temperature: 0.2 });
};

/**
 * Fix code bugs - Find and fix errors
 */
export const fixCode = async (code, model = 'codellama') => {
  const prompt = `Analysiere den folgenden Code auf Fehler (Syntax, Logik, Best Practices). Gib den korrigierten Code zurück.

Code:
\`\`\`
${code}
\`\`\`

Korrigierter Code:`;

  return generateText(prompt, model, { temperature: 0.2 });
};

/**
 * Refactor code - Improve code quality
 */
export const refactorCode = async (code, model = 'codellama') => {
  const prompt = `Refaktoriere den folgenden Code. Verbessere Lesbarkeit, Performance und Best Practices. Behalte die Funktionalität bei.

Code:
\`\`\`
${code}
\`\`\`

Refaktorierter Code:`;

  return generateText(prompt, model, { temperature: 0.3 });
};

/**
 * Add comments to code
 */
export const addCodeComments = async (code, model = 'codellama') => {
  const prompt = `Füge hilfreiche Kommentare zum folgenden Code hinzu. Erkläre komplexe Teile, aber vermeide offensichtliche Kommentare.

Code:
\`\`\`
${code}
\`\`\`

Code mit Kommentaren:`;

  return generateText(prompt, model, { temperature: 0.3 });
};

/**
 * Generate unit tests for code
 */
export const generateTests = async (code, model = 'codellama') => {
  const prompt = `Generiere Unit-Tests für den folgenden Code. Verwende Jest/Vitest Syntax. Decke wichtige Testfälle ab.

Code:
\`\`\`
${code}
\`\`\`

Unit-Tests:`;

  return generateText(prompt, model, { temperature: 0.4 });
};

/**
 * Convert code between languages
 */
export const convertCode = async (code, fromLang, toLang, model = 'codellama') => {
  const prompt = `Konvertiere den folgenden ${fromLang}-Code nach ${toLang}. Behalte die Funktionalität bei.

${fromLang} Code:
\`\`\`
${code}
\`\`\`

${toLang} Code:`;

  return generateText(prompt, model, { temperature: 0.3 });
};

/**
 * Streaming version for real-time updates
 * @param {string} prompt - The prompt
 * @param {function} onChunk - Callback for each chunk
 * @param {string} model - Model to use
 */
export const generateTextStream = async (prompt, onChunk, model = DEFAULT_MODEL) => {
  try {
    const response = await fetch(`${OLLAMA_API_URL}/api/generate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        model,
        prompt,
        stream: true
      })
    });

    if (!response.ok) {
      throw new Error(`Ollama API error: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value);
      const lines = chunk.split('\n').filter(line => line.trim());

      for (const line of lines) {
        try {
          const data = JSON.parse(line);
          if (data.response) {
            onChunk(data.response);
          }
          if (data.done) {
            return { success: true };
          }
        } catch (e) {
          console.warn('Failed to parse chunk:', e);
        }
      }
    }

    return { success: true };
  } catch (error) {
    console.error('Streaming error:', error);
    return { success: false, error: error.message };
  }
};

export default {
  // Core functions
  checkOllamaStatus,
  generateText,
  generateTextStream,

  // Text features
  improveText,
  continueText,
  summarizeText,
  suggestMarkdownFormatting,

  // Code features (best with CodeLlama)
  explainCode,
  completeCode,
  fixCode,
  refactorCode,
  addCodeComments,
  generateTests,
  convertCode
};
